import { List, ListItem, ListProps, Tooltip } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import BrokerIcon from "components/Shared/BrokerIcon";

const availableBrokerFragment = gql(/* GraphQL */ `
  fragment AvailableBrokerForIcons on AvailableBroker {
    broker {
      id
      name
      ...BrokerIcon
    }
  }
`);

const brandRetailerFragment = gql(/* GraphQL */ `
  fragment BrandRetailerBrokerIcons on BrandRetailer {
    availableBrokersList {
      ...AvailableBrokerForIcons
    }
  }
`);

const retailerFragment = gql(/* GraphQL */ `
  fragment RetailerBrokerIcons on Retailer {
    availableBrokersForBrandList(brandId: $brandId) {
      ...AvailableBrokerForIcons
    }
  }
`);

type AvailableBrokerForIconsFragment = FragmentType<
  typeof availableBrokerFragment
>;

type BrandRetailerBrokerIconsFragment = FragmentType<
  typeof brandRetailerFragment
>;

export type RetailerBrokerIconsFragment = FragmentType<typeof retailerFragment>;

type BrokerIconsListProps = ListProps & {
  availableBrokers: AvailableBrokerForIconsFragment[] | null | undefined;
};

export function BrokerIconsList({
  availableBrokers: availableBrokersFragment,
  ...rest
}: BrokerIconsListProps) {
  const availableBrokers = getFragmentData(
    availableBrokerFragment,
    availableBrokersFragment,
  );
  if (!availableBrokers?.length) return null;
  return (
    <List display="inline-flex" alignItems="baseline" gap={1} {...rest}>
      {availableBrokers.map((availableBroker) => {
        const { broker } = availableBroker;

        return (
          <Tooltip
            key={broker.id}
            label={`This retailer is a ${broker.name} account`}
            hasArrow
            whiteSpace="nowrap"
            maxW="50vw"
          >
            <ListItem
              display="flex"
              alignItems="center"
              justifyContent="center"
              flex="0 0 1em"
            >
              <BrokerIcon broker={broker} />
            </ListItem>
          </Tooltip>
        );
      })}
    </List>
  );
}

type RetailerProps = ListProps & {
  retailer: RetailerBrokerIconsFragment;
};

export default function RetailerBrokerIcons({
  retailer: retailerFragmentData,
  ...rest
}: RetailerProps) {
  const data = getFragmentData(retailerFragment, retailerFragmentData);
  return (
    <BrokerIconsList
      availableBrokers={data.availableBrokersForBrandList}
      {...rest}
    />
  );
}

type BrandRetailerProps = ListProps & {
  brandRetailer: BrandRetailerBrokerIconsFragment;
};

export function BrandRetailerBrokerIcons({
  brandRetailer: brandRetailerFragmentData,
  ...rest
}: BrandRetailerProps) {
  const data = getFragmentData(
    brandRetailerFragment,
    brandRetailerFragmentData,
  );
  return (
    <BrokerIconsList availableBrokers={data.availableBrokersList} {...rest} />
  );
}
